import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  message,
  Typography,
  Tooltip,
  Tag,
  Upload,
  Image,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  GlobalOutlined,
  UploadOutlined,
  LoadingOutlined,
  FormOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PagesWrapper from '../../../PagesWrapper';
import countriesService from '../../../services/countriesService';
import surveysService from '../../../services/surveysService';
import styles from './Countries.module.css';

const { Title, Text } = Typography;

/**
 * Countries Management Page
 * Admin page for CRUD operations on country metadata
 */
const Countries = () => {
  const { t } = useTranslation();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [form] = Form.useForm();
  
  // Flag image states - stores file locally until form submission
  const [flagImageFile, setFlagImageFile] = useState(null);
  const [flagImagePreview, setFlagImagePreview] = useState(null);
  
  // Survey assignment modal states
  const [surveyModalVisible, setSurveyModalVisible] = useState(false);
  const [surveyModalCountry, setSurveyModalCountry] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [surveysLoading, setSurveysLoading] = useState(false);
  const [surveysPagination, setSurveysPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [surveysSearch, setSurveysSearch] = useState('');
  const [assigningSurvey, setAssigningSurvey] = useState(false);

  /**
   * Fetches countries from the API
   */
  const fetchCountries = useCallback(async (page = 1, pageSize = 10, search = '') => {
    setLoading(true);
    try {
      const response = await countriesService.getCountries({
        page,
        pageSize,
        search: search || undefined,
      });
      setCountries(response.items || []);
      setPagination({
        current: response.page,
        pageSize: response.pageSize,
        total: response.totalCount,
      });
    } catch (error) {
      message.error(t('admin.countries.fetchError', 'Failed to load countries'));
      console.error('Failed to fetch countries:', error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * Fetches available surveys for registration assignment (admin endpoint - all surveys)
   */
  const fetchSurveys = useCallback(async (page = 1, pageSize = 10, search = '') => {
    setSurveysLoading(true);
    try {
      const response = await surveysService.getAllSurveys({ 
        page, 
        pageSize, 
        search: search || undefined 
      });
      setSurveys(response.items || []);
      setSurveysPagination({
        current: response.page,
        pageSize: response.pageSize,
        total: response.totalCount,
      });
    } catch (error) {
      console.error('Failed to fetch surveys:', error);
      message.error(t('admin.countries.surveysFetchError', 'Failed to load surveys'));
    } finally {
      setSurveysLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCountries(pagination.current, pagination.pageSize, searchText);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handles table pagination changes
   */
  const handleTableChange = (newPagination) => {
    fetchCountries(newPagination.current, newPagination.pageSize, searchText);
  };

  /**
   * Handles search input
   */
  const handleSearch = (value) => {
    setSearchText(value);
    fetchCountries(1, pagination.pageSize, value);
  };

  /**
   * Opens the modal for creating/editing a country
   */
  const openModal = (country = null) => {
    setEditingCountry(country);
    if (country) {
      form.setFieldsValue({
        name: country.name,
        language: country.language,
      });
      // For editing, show the existing flag image (no new file yet)
      setFlagImageFile(null);
      setFlagImagePreview(country.flagImage?.relativeUrl || null);
    } else {
      form.resetFields();
      setFlagImageFile(null);
      setFlagImagePreview(null);
    }
    setModalVisible(true);
  };

  /**
   * Closes the modal
   */
  const closeModal = () => {
    setModalVisible(false);
    setEditingCountry(null);
    setFlagImageFile(null);
    setFlagImagePreview(null);
    form.resetFields();
  };

  /**
   * Handles form submission for create/update
   */
  const handleSubmit = async (values) => {
    // For create: flag image is required
    // For update: flag image is optional (keep existing if not changed)
    if (!editingCountry && !flagImageFile) {
      message.error(t('admin.countries.form.flagRequired', 'Please select a flag image'));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: values.name,
        language: values.language,
        flagImage: flagImageFile, // Will be null if not changed during edit
      };

      if (editingCountry) {
        await countriesService.updateCountry(editingCountry.id, payload);
        message.success(t('admin.countries.updateSuccess', 'Country updated successfully'));
      } else {
        await countriesService.createCountry(payload);
        message.success(t('admin.countries.createSuccess', 'Country created successfully'));
      }
      
      closeModal();
      fetchCountries(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      message.error(errorMessage || t('admin.countries.saveError', 'Failed to save country'));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handles flag image selection (stores file locally, doesn't upload)
   */
  const handleFlagSelect = (file) => {
    // Validate file type
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error(t('admin.countries.form.flagImageOnly', 'Please select an image file'));
      return false;
    }

    // Validate file size (max 5MB)
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error(t('admin.countries.form.flagSizeLimit', 'Image must be smaller than 5MB'));
      return false;
    }

    // Store the file and create preview
    setFlagImageFile(file);
    
    // Create local preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setFlagImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    return false; // Prevent default upload behavior
  };

  /**
   * Removes the selected flag image
   */
  const handleRemoveFlag = () => {
    setFlagImageFile(null);
    // If editing, restore original image preview
    if (editingCountry) {
      setFlagImagePreview(editingCountry.flagImage?.relativeUrl || null);
    } else {
      setFlagImagePreview(null);
    }
  };

  /**
   * Handles country deletion
   */
  const handleDelete = async (id) => {
    try {
      await countriesService.deleteCountry(id);
      message.success(t('admin.countries.deleteSuccess', 'Country deleted successfully'));
      fetchCountries(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error(t('admin.countries.deleteError', 'Failed to delete country'));
    }
  };

  /**
   * Opens the survey assignment modal
   */
  const openSurveyModal = (country) => {
    setSurveyModalCountry(country);
    setSurveyModalVisible(true);
    setSurveysSearch('');
    fetchSurveys(1, 10, '');
  };

  /**
   * Closes the survey assignment modal
   */
  const closeSurveyModal = () => {
    setSurveyModalVisible(false);
    setSurveyModalCountry(null);
    setSurveys([]);
    setSurveysSearch('');
  };

  /**
   * Handles survey selection for a country
   */
  const handleAssignSurvey = async (surveyId) => {
    if (!surveyModalCountry) return;
    
    setAssigningSurvey(true);
    try {
      await countriesService.assignRegistrationSurvey(surveyModalCountry.id, surveyId);
      message.success(t('admin.countries.surveyAssignSuccess', 'Survey assigned successfully'));
      closeSurveyModal();
      fetchCountries(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error(t('admin.countries.surveyAssignError', 'Failed to assign survey'));
    } finally {
      setAssigningSurvey(false);
    }
  };

  /**
   * Clears the survey assignment for a country
   */
  const handleClearSurvey = async () => {
    if (!surveyModalCountry) return;
    
    setAssigningSurvey(true);
    try {
      await countriesService.assignRegistrationSurvey(surveyModalCountry.id, null);
      message.success(t('admin.countries.surveyClearSuccess', 'Survey assignment cleared'));
      closeSurveyModal();
      fetchCountries(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error(t('admin.countries.surveyClearError', 'Failed to clear survey assignment'));
    } finally {
      setAssigningSurvey(false);
    }
  };

  /**
   * Handles survey table pagination changes
   */
  const handleSurveysTableChange = (newPagination) => {
    fetchSurveys(newPagination.current, newPagination.pageSize, surveysSearch);
  };

  /**
   * Handles survey search
   */
  const handleSurveysSearch = (value) => {
    setSurveysSearch(value);
    fetchSurveys(1, surveysPagination.pageSize, value);
  };

  /**
   * Survey table columns for the assignment modal
   */
  const surveyColumns = [
    {
      title: t('admin.countries.surveyColumns.name', 'Survey Name'),
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          {name}
          {record.isPublished && <Tag color="green">{t('admin.countries.surveyColumns.published', 'Published')}</Tag>}
          {record.id === surveyModalCountry?.registrationSurveyId && (
            <Tag color="blue">{t('admin.countries.surveyColumns.current', 'Current')}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('admin.countries.surveyColumns.owner', 'Owner'),
      dataIndex: 'ownerName',
      key: 'owner',
      width: 150,
    },
    {
      title: t('admin.countries.surveyColumns.questions', 'Questions'),
      dataIndex: 'questionCount',
      key: 'questions',
      width: 100,
      align: 'center',
    },
    {
      title: t('admin.countries.surveyColumns.actions', 'Actions'),
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        record.id === surveyModalCountry?.registrationSurveyId ? (
          <Tag color="blue">{t('admin.countries.surveyColumns.selected', 'Selected')}</Tag>
        ) : (
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => handleAssignSurvey(record.id)}
            loading={assigningSurvey}
          >
            {t('common.select', 'Select')}
          </Button>
        )
      ),
    },
  ];

  /**
   * Table columns configuration
   */
  const columns = [
    {
      title: t('admin.countries.columns.flag', 'Flag'),
      dataIndex: 'flagImage',
      key: 'flag',
      width: 100,
      align: 'center',
      render: (flagImage) => (
        flagImage?.relativeUrl ? (
          <Image
            src={flagImage.relativeUrl}
            alt={flagImage.altText || 'Country flag'}
            width={48}
            height={32}
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        ) : (
          <span style={{ color: '#999' }}>—</span>
        )
      ),
    },
    {
      title: t('admin.countries.columns.name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t('admin.countries.columns.language', 'Language'),
      dataIndex: 'language',
      key: 'language',
      width: 150,
      render: (language) => <Tag color="green">{language}</Tag>,
    },
    {
      title: t('admin.countries.columns.registrationSurvey', 'Registration Survey'),
      dataIndex: 'registrationSurveyName',
      key: 'registrationSurvey',
      width: 200,
      render: (surveyName) => (
        surveyName ? (
          <Tag icon={<FormOutlined />} color="blue">{surveyName}</Tag>
        ) : (
          <span style={{ color: '#999' }}>—</span>
        )
      ),
    },
    {
      title: t('admin.countries.columns.actions', 'Actions'),
      key: 'actions',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('admin.countries.assignSurvey', 'Assign Survey')}>
            <Button
              type="text"
              icon={<FormOutlined />}
              onClick={() => openSurveyModal(record)}
            />
          </Tooltip>
          <Tooltip title={t('common.edit', 'Edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('admin.countries.deleteConfirm', 'Are you sure you want to delete this country?')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes', 'Yes')}
            cancelText={t('common.no', 'No')}
          >
            <Tooltip title={t('common.delete', 'Delete')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PagesWrapper>
      <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.titleSection}>
              <GlobalOutlined className={styles.titleIcon} />
              <Title level={2} className={styles.title}>
                {t('admin.countries.title', 'Countries')}
              </Title>
            </div>
            <Text type="secondary">
              {t('admin.countries.description', 'Manage country metadata for internationalization')}
            </Text>
          </div>

          <div className={styles.toolbar}>
            <Space>
              <Input.Search
                placeholder={t('admin.countries.searchPlaceholder', 'Search countries...')}
                allowClear
                onSearch={handleSearch}
                style={{ width: 300 }}
                prefix={<SearchOutlined />}
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchCountries(pagination.current, pagination.pageSize, searchText)}
              >
                {t('common.refresh', 'Refresh')}
              </Button>
            </Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openModal()}
            >
              {t('admin.countries.addCountry', 'Add Country')}
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={countries}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total, range) =>
                t('common.pagination', '{{start}}-{{end}} of {{total}} items', {
                  start: range[0],
                  end: range[1],
                  total,
                }),
            }}
            onChange={handleTableChange}
          />

        <Modal
          title={editingCountry
            ? t('admin.countries.editTitle', 'Edit Country')
            : t('admin.countries.addTitle', 'Add Country')
          }
          open={modalVisible}
          onCancel={closeModal}
          footer={null}
          destroyOnClose
          width={500}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              label={t('admin.countries.form.flag', 'Flag Image')}
              required={!editingCountry}
              help={
                editingCountry 
                  ? t('admin.countries.form.flagHelpEdit', 'Select a new image to change the flag, or leave as is')
                  : t('admin.countries.form.flagHelp', 'Select a flag image (PNG, JPG, SVG, max 5MB)')
              }
            >
              <div className={styles.flagUploadContainer}>
                {flagImagePreview ? (
                  <div className={styles.flagPreview}>
                    <Image
                      src={flagImagePreview}
                      alt="Flag preview"
                      width={80}
                      height={54}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                      preview={!flagImageFile}
                    />
                    {flagImageFile && (
                      <Button
                        type="link"
                        danger
                        size="small"
                        onClick={handleRemoveFlag}
                      >
                        {t('common.remove', 'Remove')}
                      </Button>
                    )}
                    {!flagImageFile && editingCountry && (
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={handleFlagSelect}
                      >
                        <Button type="link" size="small">
                          {t('admin.countries.form.changeFlag', 'Change')}
                        </Button>
                      </Upload>
                    )}
                  </div>
                ) : (
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={handleFlagSelect}
                  >
                    <Button icon={<UploadOutlined />}>
                      {t('admin.countries.form.selectFlag', 'Select Flag Image')}
                    </Button>
                  </Upload>
                )}
              </div>
            </Form.Item>

            <Form.Item
              name="name"
              label={t('admin.countries.form.name', 'Country Name')}
              rules={[
                { required: true, message: t('admin.countries.form.nameRequired', 'Please enter country name') },
                { max: 100, message: t('admin.countries.form.nameMaxLength', 'Name must be at most 100 characters') },
              ]}
            >
              <Input placeholder={t('admin.countries.form.namePlaceholder', 'e.g., Netherlands')} />
            </Form.Item>

            <Form.Item
              name="language"
              label={t('admin.countries.form.language', 'Primary Language')}
              rules={[
                { required: true, message: t('admin.countries.form.languageRequired', 'Please enter the primary language') },
                { max: 100, message: t('admin.countries.form.languageMaxLength', 'Language must be at most 100 characters') },
              ]}
            >
              <Input placeholder={t('admin.countries.form.languagePlaceholder', 'e.g., Dutch, German, English')} />
            </Form.Item>

            <Form.Item className={styles.formActions}>
              <Space>
                <Button onClick={closeModal} disabled={submitting}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  {editingCountry
                    ? t('common.save', 'Save')
                    : t('common.create', 'Create')
                  }
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Survey Assignment Modal */}
        <Modal
          title={
            surveyModalCountry 
              ? t('admin.countries.surveyModalTitle', 'Assign Registration Survey to {{country}}', { country: surveyModalCountry.name })
              : t('admin.countries.surveyModalTitleGeneric', 'Assign Registration Survey')
          }
          open={surveyModalVisible}
          onCancel={closeSurveyModal}
          footer={null}
          destroyOnClose
          width={800}
        >
          <div className={styles.surveyModalContent}>
            {surveyModalCountry?.registrationSurveyId && (
              <div className={styles.currentSurvey}>
                <Text strong>{t('admin.countries.currentSurvey', 'Current Survey:')}</Text>
                <Tag icon={<FormOutlined />} color="blue" style={{ marginLeft: 8 }}>
                  {surveyModalCountry.registrationSurveyName}
                </Tag>
                <Button
                  type="link"
                  danger
                  icon={<CloseOutlined />}
                  onClick={handleClearSurvey}
                  loading={assigningSurvey}
                >
                  {t('admin.countries.clearSurvey', 'Clear Assignment')}
                </Button>
              </div>
            )}
            
            <div className={styles.surveySearchBar}>
              <Input.Search
                placeholder={t('admin.countries.searchSurveys', 'Search surveys...')}
                allowClear
                onSearch={handleSurveysSearch}
                style={{ width: 300 }}
                prefix={<SearchOutlined />}
              />
            </div>

            <Table
              columns={surveyColumns}
              dataSource={surveys}
              rowKey="id"
              loading={surveysLoading}
              size="small"
              pagination={{
                ...surveysPagination,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  t('common.pagination', '{{start}}-{{end}} of {{total}} items', {
                    start: range[0],
                    end: range[1],
                    total,
                  }),
              }}
              onChange={handleSurveysTableChange}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('admin.countries.noSurveys', 'No surveys found')}
                  />
                ),
              }}
            />
          </div>
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default Countries;
